import { memo, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// Import Types
import type { DropdownOption } from 'types';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useHasPermission from 'hooks/UseHasPermission';
import { useMenuItem } from 'hooks/UseMenuItem';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Constants
import { l, PARTNER_SETTINGS } from 'constants/common';
// Import Enums
import { MenuItemIdEnum } from 'enums';
// Import Components
import { CJInfoBlockLabelValueWrapper } from 'components/CustomerJourney/infoBlocks';

const CJSubscriptionsInfoBlock = ({ data }) => {
    const { t } = useTranslation();

    const { doGetRequest } = useRequest();
    const { hasPartnerSettings } = useHasPermission();

    const isAllowTemplateCategory = hasPartnerSettings(PARTNER_SETTINGS.IsAllowTemplateCategory);

    const { convertedData: templateCategories, isLoading: isTemplateCategoryLoading } = useMenuItem(
        isAllowTemplateCategory ? MenuItemIdEnum.TemplateCategory : null,
    );

    const [channelsList, setChannelsList] = useState<DropdownOption<number>[]>([]);
    const [isChannelsListLoading, setIsLoadingChannelsList] = useState(true);

    const channelName = useMemo(() => {
        return channelsList.find((item) => item.value === data.channel)?.label || t(l.NA);
    }, [channelsList, data.channel, t]);

    const templateCategory = useMemo(() => {
        return templateCategories.find((item) => +item.value === data.category)?.label || t(l.NA);
    }, [templateCategories, data.category, t]);

    const { getChannelsListRequest } = useCancelableRequests({
        getChannelsListRequest: UtilsHttpService.getChannelsList,
    });

    const init = () => {
        doGetRequest(getChannelsListRequest.request, {
            successCallback: (data) => {
                setChannelsList(
                    data.map(({ Id, Name }) => {
                        return {
                            value: +Id,
                            label: Name,
                        };
                    }),
                );
                setIsLoadingChannelsList(false);
            },
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <>
            <CJInfoBlockLabelValueWrapper label={t(l.Channel)} value={channelName} isLoading={isChannelsListLoading} />
            {isAllowTemplateCategory && (
                <CJInfoBlockLabelValueWrapper
                    label={t(l.Category)}
                    value={templateCategory}
                    isLoading={isTemplateCategoryLoading}
                />
            )}
        </>
    );
};

CJSubscriptionsInfoBlock.propTypes = {
    data: PropTypes.object.isRequired,
};

export default memo(CJSubscriptionsInfoBlock);
